import { LoyaltyProgram, Transaction } from '../types';
import { differenceInDays, subMonths, format } from 'date-fns';

export interface PortfolioHealthScore {
  overall: number; // 0-100
  diversification: number;
  activityLevel: number;
  expiryRisk: number;
  valueOptimization: number;
  insights: string[];
}

export interface EarningAnalysis {
  topEarners: Array<{
    programId: string;
    programName: string;
    totalEarned: number;
    lastActivity: string;
    earnRate: number;
  }>;
  monthlyTrend: number;
  totalEarned30Days: number;
  insights: string[];
}

export interface RedemptionOpportunity {
  programId: string;
  programName: string;
  currentBalance: number;
  suggestedRedemption: string;
  estimatedValue: number;
  reasoning: string;
  urgency: 'high' | 'medium' | 'low';
}

export interface RiskAlert {
  type: 'expiring' | 'low_balance' | 'inactive' | 'devaluation';
  severity: 'critical' | 'warning' | 'info';
  programId: string;
  programName: string;
  message: string;
  actionRequired: string;
  daysUntilImpact?: number;
}

export interface SmartRecommendation {
  type: 'transfer' | 'spending' | 'redemption' | 'earning';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  expectedValue: number;
  actionItems: string[];
}

class AIAnalysisService {
  /**
   * Calculate overall portfolio health score based on real user data
   */
  calculatePortfolioHealth(programs: LoyaltyProgram[], transactions: Transaction[]): PortfolioHealthScore {
    if (programs.length === 0) {
      return {
        overall: 0,
        diversification: 0,
        activityLevel: 0,
        expiryRisk: 100,
        valueOptimization: 0,
        insights: ['Add your first loyalty program to see portfolio analysis']
      };
    }

    const insights: string[] = [];
    
    // 1. Diversification Score (0-100)
    const programTypes = new Set(programs.map(p => p.type));
    const diversificationScore = Math.min(100, (programTypes.size / 3) * 100);
    
    if (diversificationScore < 70) {
      const missing = [];
      if (!programTypes.has('airline')) missing.push('airline programs');
      if (!programTypes.has('hotel')) missing.push('hotel programs');
      if (!programTypes.has('credit_card')) missing.push('credit cards');
      if (missing.length > 0) {
        insights.push(`Consider adding ${missing.join(' and ')} for better diversification`);
      }
    }

    // 2. Activity Level Score (0-100)
    const thirtyDaysAgo = subMonths(new Date(), 1);
    const recentTransactions = transactions.filter(t => 
      new Date(t.date) >= thirtyDaysAgo
    );
    const activityScore = programs.length > 0 
      ? Math.min(100, (recentTransactions.length / programs.length) * 50)
      : 0;
    
    if (activityScore < 30 && programs.length > 0) {
      insights.push('Low portfolio activity detected. Use your programs more actively to maximize value.');
    }

    // 3. Expiry Risk Score (0-100, higher is better)
    const programsWithExpiry = programs.filter(p => p.expiryDate);
    const criticalExpiryPrograms = programsWithExpiry.filter(p => {
      const daysUntil = differenceInDays(new Date(p.expiryDate!), new Date());
      return daysUntil < 90 && daysUntil > 0;
    });
    const expiryRiskScore = programsWithExpiry.length > 0 
      ? 100 - ((criticalExpiryPrograms.length / programsWithExpiry.length) * 100)
      : 100;
    
    if (criticalExpiryPrograms.length > 0) {
      const totalPointsExpiring = criticalExpiryPrograms.reduce((sum, p) => sum + (p.points || 0), 0);
      insights.push(`${totalPointsExpiring.toLocaleString()} points expiring within 90 days across ${criticalExpiryPrograms.length} program(s). Take action now!`);
    }

    // 4. Value Optimization Score (0-100)
    const totalPoints = programs.reduce((sum, p) => sum + (p.points || 0), 0);
    const avgPointsPerProgram = totalPoints / programs.length;
    const wellBalancedPrograms = programs.filter(p => (p.points || 0) > avgPointsPerProgram * 0.3).length;
    const valueOptimizationScore = (wellBalancedPrograms / programs.length) * 100;
    
    if (valueOptimizationScore < 60) {
      const lowBalanceCount = programs.filter(p => (p.points || 0) < avgPointsPerProgram * 0.3).length;
      insights.push(`${lowBalanceCount} program(s) have low balances. Consider consolidating points through transfers.`);
    }

    // 5. Overall Score (weighted average)
    const overall = Math.round(
      diversificationScore * 0.25 +
      activityScore * 0.20 +
      expiryRiskScore * 0.30 +
      valueOptimizationScore * 0.25
    );

    return {
      overall,
      diversification: Math.round(diversificationScore),
      activityLevel: Math.round(activityScore),
      expiryRisk: Math.round(expiryRiskScore),
      valueOptimization: Math.round(valueOptimizationScore),
      insights
    };
  }

  /**
   * Analyze earning patterns from real transaction data
   */
  analyzeEarningPatterns(programs: LoyaltyProgram[], transactions: Transaction[]): EarningAnalysis {
    const insights: string[] = [];
    const thirtyDaysAgo = subMonths(new Date(), 1);
    const sixtyDaysAgo = subMonths(new Date(), 2);

    const earnedTransactions = transactions.filter(t => t.type === 'earned');

    if (earnedTransactions.length === 0) {
      return {
        topEarners: [],
        monthlyTrend: 0,
        totalEarned30Days: 0,
        insights: ['No earning transactions recorded yet. Add transactions to see earning analysis.']
      };
    }

    // Calculate earning by program
    const earningsByProgram = new Map<string, { earned: number; transactions: number; lastActivity: Date }>();
    
    earnedTransactions.forEach(t => {
      const existing = earningsByProgram.get(t.programId) || { earned: 0, transactions: 0, lastActivity: new Date(0) };
      earningsByProgram.set(t.programId, {
        earned: existing.earned + t.points,
        transactions: existing.transactions + 1,
        lastActivity: new Date(Math.max(existing.lastActivity.getTime(), new Date(t.date).getTime()))
      });
    });

    // Top earners
    const topEarners = Array.from(earningsByProgram.entries())
      .map(([programId, data]) => {
        const program = programs.find(p => p.id === programId);
        return {
          programId,
          programName: program?.name || 'Unknown Program',
          totalEarned: data.earned,
          lastActivity: format(data.lastActivity, 'MMM dd, yyyy'),
          earnRate: Math.round(data.earned / data.transactions)
        };
      })
      .sort((a, b) => b.totalEarned - a.totalEarned)
      .slice(0, 5);

    if (topEarners.length > 0) {
      insights.push(`${topEarners[0].programName} is your top earning program with ${topEarners[0].totalEarned.toLocaleString()} points earned.`);
    }

    // Monthly trend
    const last30Days = earnedTransactions.filter(t => new Date(t.date) >= thirtyDaysAgo);
    const previous30Days = earnedTransactions.filter(t => 
      new Date(t.date) >= sixtyDaysAgo && new Date(t.date) < thirtyDaysAgo
    );
    
    const totalEarned30Days = last30Days.reduce((sum, t) => sum + t.points, 0);
    const totalEarnedPrevious30Days = previous30Days.reduce((sum, t) => sum + t.points, 0);
    
    const monthlyTrend = totalEarnedPrevious30Days > 0 
      ? ((totalEarned30Days - totalEarnedPrevious30Days) / totalEarnedPrevious30Days) * 100
      : totalEarned30Days > 0 ? 100 : 0;

    if (monthlyTrend > 10) {
      insights.push(`Your earning rate increased by ${monthlyTrend.toFixed(0)}% this month. Excellent progress!`);
    } else if (monthlyTrend < -10) {
      insights.push(`Your earning rate decreased by ${Math.abs(monthlyTrend).toFixed(0)}% this month. Consider increasing activity.`);
    } else if (monthlyTrend >= -10 && monthlyTrend <= 10) {
      insights.push('Your earning rate is stable. Look for bonus opportunities to increase earnings.');
    }

    return {
      topEarners,
      monthlyTrend: Math.round(monthlyTrend),
      totalEarned30Days,
      insights
    };
  }

  /**
   * Find redemption opportunities based on actual balances
   */
  findRedemptionOpportunities(programs: LoyaltyProgram[]): RedemptionOpportunity[] {
    const opportunities: RedemptionOpportunity[] = [];

    programs.forEach(program => {
      const balance = program.points || 0;
      
      // Skip programs with very low balances
      if (balance < 5000) return;

      let suggestedRedemption = '';
      let estimatedValue = 0;
      let reasoning = '';
      let urgency: 'high' | 'medium' | 'low' = 'low';

      // Airline programs
      if (program.type === 'airline') {
        if (balance >= 100000) {
          suggestedRedemption = 'Business class international flight';
          estimatedValue = balance * 0.02;
          reasoning = 'You have enough miles for a premium long-haul redemption with excellent value per point.';
          urgency = 'medium';
        } else if (balance >= 50000) {
          suggestedRedemption = 'Business class short-haul or Economy international';
          estimatedValue = balance * 0.018;
          reasoning = 'Good balance for premium regional travel or international economy.';
          urgency = 'medium';
        } else if (balance >= 25000) {
          suggestedRedemption = 'Economy international flight';
          estimatedValue = balance * 0.015;
          reasoning = 'Sufficient for international economy award tickets.';
          urgency = 'low';
        } else if (balance >= 12500) {
          suggestedRedemption = 'Domestic round-trip flight';
          estimatedValue = balance * 0.014;
          reasoning = 'Perfect for domestic travel redemptions.';
          urgency = 'low';
        } else {
          suggestedRedemption = 'One-way domestic flight or save for better redemption';
          estimatedValue = balance * 0.012;
          reasoning = 'Consider earning more miles for better redemption value.';
          urgency = 'low';
        }
      }

      // Hotel programs
      if (program.type === 'hotel') {
        if (balance >= 150000) {
          suggestedRedemption = '5+ nights at luxury properties';
          estimatedValue = balance * 0.008;
          reasoning = 'Enough points for extended stays at premium hotels.';
          urgency = 'medium';
        } else if (balance >= 70000) {
          suggestedRedemption = '3-4 nights at upscale hotels';
          estimatedValue = balance * 0.007;
          reasoning = 'Great for a weekend getaway at higher-tier properties.';
          urgency = 'low';
        } else if (balance >= 30000) {
          suggestedRedemption = '2-3 nights at mid-tier hotels';
          estimatedValue = balance * 0.006;
          reasoning = 'Perfect for short trips or business travel.';
          urgency = 'low';
        } else if (balance >= 15000) {
          suggestedRedemption = '1-2 nights at standard hotels';
          estimatedValue = balance * 0.005;
          reasoning = 'Suitable for quick overnight stays.';
          urgency = 'low';
        } else {
          suggestedRedemption = 'Save for better redemption opportunities';
          estimatedValue = balance * 0.004;
          reasoning = 'Continue earning points for more valuable stays.';
          urgency = 'low';
        }
      }

      // Credit card points
      if (program.type === 'credit_card') {
        if (balance >= 100000) {
          suggestedRedemption = 'Transfer to airline partner for premium cabin';
          estimatedValue = balance * 0.02;
          reasoning = 'Maximize value by transferring to airline partners for business/first class awards.';
          urgency = 'medium';
        } else if (balance >= 50000) {
          suggestedRedemption = 'Transfer to travel partners or book through portal';
          estimatedValue = balance * 0.015;
          reasoning = 'Flexible redemption options with good value potential.';
          urgency = 'low';
        } else if (balance >= 25000) {
          suggestedRedemption = 'Travel booking or transfer to partners';
          estimatedValue = balance * 0.013;
          reasoning = 'Enough for meaningful travel redemptions.';
          urgency = 'low';
        } else if (balance >= 10000) {
          suggestedRedemption = 'Statement credit or small travel purchase';
          estimatedValue = balance * 0.01;
          reasoning = 'Consider cash back or save for larger redemption.';
          urgency = 'low';
        } else {
          suggestedRedemption = 'Continue earning for better value';
          estimatedValue = balance * 0.01;
          reasoning = 'Build your balance for more rewarding redemptions.';
          urgency = 'low';
        }
      }

      // Check expiry urgency
      if (program.expiryDate) {
        const daysUntilExpiry = differenceInDays(new Date(program.expiryDate), new Date());
        if (daysUntilExpiry < 90 && daysUntilExpiry > 0) {
          urgency = 'high';
          reasoning += ` ⚠️ URGENT: Points expire in ${daysUntilExpiry} days!`;
        } else if (daysUntilExpiry < 180 && daysUntilExpiry > 0) {
          if (urgency === 'low') urgency = 'medium';
          reasoning += ` Note: Points expire in ${daysUntilExpiry} days.`;
        }
      }

      if (suggestedRedemption) {
        opportunities.push({
          programId: program.id,
          programName: program.name,
          currentBalance: balance,
          suggestedRedemption,
          estimatedValue,
          reasoning,
          urgency
        });
      }
    });

    return opportunities.sort((a, b) => {
      const urgencyOrder = { high: 3, medium: 2, low: 1 };
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[b.urgency] - urgencyOrder[a.urgency];
      }
      return b.estimatedValue - a.estimatedValue;
    });
  }

  /**
   * Identify portfolio risks from real data
   */
  identifyRisks(programs: LoyaltyProgram[], transactions: Transaction[]): RiskAlert[] {
    const alerts: RiskAlert[] = [];
    const ninetyDaysAgo = subMonths(new Date(), 3);

    programs.forEach(program => {
      const balance = program.points || 0;

      // Expiring points
      if (program.expiryDate) {
        const daysUntilExpiry = differenceInDays(new Date(program.expiryDate), new Date());
        if (daysUntilExpiry > 0 && daysUntilExpiry < 90 && balance > 0) {
          alerts.push({
            type: 'expiring',
            severity: daysUntilExpiry < 30 ? 'critical' : 'warning',
            programId: program.id,
            programName: program.name,
            message: `${balance.toLocaleString()} ${program.currency || 'points'} expiring in ${daysUntilExpiry} days`,
            actionRequired: daysUntilExpiry < 30 
              ? 'Book a redemption immediately or extend expiry' 
              : 'Plan a redemption or earn more points to extend validity',
            daysUntilImpact: daysUntilExpiry
          });
        }
      }

      // Low balance warnings
      if (balance > 0 && balance < 5000 && program.type === 'credit_card') {
        alerts.push({
          type: 'low_balance',
          severity: 'info',
          programId: program.id,
          programName: program.name,
          message: `Low balance: ${balance.toLocaleString()} points`,
          actionRequired: 'Consider consolidating or using this card more frequently to build balance'
        });
      }

      // Inactive programs (no transactions in 90 days)
      const programTransactions = transactions.filter(t => t.programId === program.id);
      const recentActivity = programTransactions.filter(t => new Date(t.date) >= ninetyDaysAgo);
      
      if (balance > 10000 && recentActivity.length === 0 && programTransactions.length > 0) {
        alerts.push({
          type: 'inactive',
          severity: 'warning',
          programId: program.id,
          programName: program.name,
          message: `No activity in 90+ days with ${balance.toLocaleString()} ${program.currency || 'points'}`,
          actionRequired: 'Use this program to prevent potential expiry or account closure'
        });
      }
    });

    return alerts.sort((a, b) => {
      const severityOrder = { critical: 3, warning: 2, info: 1 };
      return severityOrder[b.severity] - severityOrder[a.severity];
    });
  }

  /**
   * Generate smart recommendations based on user's actual portfolio
   */
  generateRecommendations(
    programs: LoyaltyProgram[], 
    transactions: Transaction[],
    portfolioHealth: PortfolioHealthScore
  ): SmartRecommendation[] {
    const recommendations: SmartRecommendation[] = [];

    if (programs.length === 0) {
      return [{
        type: 'earning',
        priority: 'high',
        title: 'Start Your Loyalty Portfolio',
        description: 'Add your first loyalty program to start earning and tracking rewards',
        expectedValue: 0,
        actionItems: [
          'Add airline programs for flights you take',
          'Add hotel programs for stays you book',
          'Add credit cards that earn transferable points'
        ]
      }];
    }

    // Transfer opportunities for credit cards
    const creditCardPrograms = programs.filter(p => p.type === 'credit_card' && (p.points || 0) >= 25000);
    const airlinePrograms = programs.filter(p => p.type === 'airline');
    
    if (creditCardPrograms.length > 0 && airlinePrograms.length > 0) {
      const topCreditCard = creditCardPrograms.sort((a, b) => (b.points || 0) - (a.points || 0))[0];
      const cardBalance = topCreditCard.points || 0;
      recommendations.push({
        type: 'transfer',
        priority: 'high',
        title: `Maximize ${topCreditCard.name} Value`,
        description: `Transfer ${cardBalance.toLocaleString()} points to airline partners for 30-100% more value on premium cabin awards`,
        expectedValue: cardBalance * 0.01,
        actionItems: [
          'Check current airline transfer bonuses',
          'Compare partner redemption rates',
          'Book award flight first, then transfer exact amount needed',
          'Consider business class for best value per point'
        ]
      });
    }

    // Diversification recommendation
    if (portfolioHealth.diversification < 70) {
      const missingTypes = [];
      const hasAirline = programs.some(p => p.type === 'airline');
      const hasHotel = programs.some(p => p.type === 'hotel');
      const hasCard = programs.some(p => p.type === 'credit_card');
      
      if (!hasAirline) missingTypes.push('airline loyalty program');
      if (!hasHotel) missingTypes.push('hotel rewards program');
      if (!hasCard) missingTypes.push('travel rewards credit card');

      if (missingTypes.length > 0) {
        recommendations.push({
          type: 'earning',
          priority: 'medium',
          title: 'Diversify Your Rewards Portfolio',
          description: `Add ${missingTypes.join(' and ')} to maximize earning opportunities across different spend categories and travel types`,
          expectedValue: 0,
          actionItems: missingTypes.map(type => `Research and add a ${type} you'll actually use`)
        });
      }
    }

    // Activity-based recommendation
    const earnedTransactions = transactions.filter(t => t.type === 'earned');
    if (earnedTransactions.length > 5) {
      const avgEarnRate = earnedTransactions.reduce((sum, t) => sum + t.points, 0) / earnedTransactions.length;
      
      if (avgEarnRate < 500) {
        recommendations.push({
          type: 'spending',
          priority: 'medium',
          title: 'Boost Your Earning Rate',
          description: `Your average earn rate is ${Math.round(avgEarnRate)} points per transaction. Use bonus categories and multipliers to earn 2-5x more`,
          expectedValue: avgEarnRate * 2 * 12,
          actionItems: [
            'Review your cards\' bonus categories',
            'Use dining cards for restaurants (3-4x points)',
            'Use travel cards for flights and hotels (2-3x points)',
            'Stack shopping portals with card earnings',
            'Take advantage of limited-time category bonuses'
          ]
        });
      }
    }

    // Large balance redemption recommendation
    const largeBalances = programs.filter(p => (p.points || 0) >= 50000);
    if (largeBalances.length > 0) {
      const topProgram = largeBalances.sort((a, b) => (b.points || 0) - (a.points || 0))[0];
      const programBalance = topProgram.points || 0;
      
      recommendations.push({
        type: 'redemption',
        priority: 'high',
        title: `Redeem ${topProgram.name} Points`,
        description: `You have ${programBalance.toLocaleString()} ${topProgram.currency || 'points'} - enough for valuable redemptions worth $${Math.round(programBalance * 0.015)}+`,
        expectedValue: programBalance * 0.015,
        actionItems: [
          'Search for available award space',
          'Book off-peak dates for better value',
          'Consider premium cabins for maximum value per point',
          'Check partner airlines/hotels for additional options',
          'Book early for popular destinations'
        ]
      });
    }

    // Consolidation recommendation
    const lowBalancePrograms = programs.filter(p => 
      (p.points || 0) > 1000 && (p.points || 0) < 15000 && p.type === 'credit_card'
    );
    if (lowBalancePrograms.length >= 2) {
      const totalLowBalance = lowBalancePrograms.reduce((sum, p) => sum + (p.points || 0), 0);
      recommendations.push({
        type: 'transfer',
        priority: 'medium',
        title: 'Consolidate Small Balances',
        description: `You have ${lowBalancePrograms.length} programs with small balances totaling ${totalLowBalance.toLocaleString()} points. Consolidate for better redemption options`,
        expectedValue: totalLowBalance * 0.005,
        actionItems: [
          'Transfer small balances to one primary program',
          'Check if points can be pooled with family accounts',
          'Use small balances for statement credits',
          'Focus future spending on 1-2 primary cards'
        ]
      });
    }

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return b.expectedValue - a.expectedValue;
    });
  }
}

export const aiAnalysisService = new AIAnalysisService();
